'use client'

import React from 'react'
import { ShieldCheck, AlertTriangle, Ban, Bot } from 'lucide-react'

/**
 * Reddit Seeding Diagrams
 * Visual support for "Reddit Brand Monitor: What AI Learns About You on Reddit".
 * Theming per CLAUDE.md: data-theme='dark', no dark: variants.
 * Brand: #008dcc #FDC304 #F90B8A #8b5cf6.
 */

interface Tier {
  label: string
  meaning: string
  care: string
  color: string
  icon: React.ReactNode
}

const TIERS: Tier[] = [
  {
    label: 'Organic',
    meaning: 'Looks like a real user in a real discussion',
    care: 'The signal you want AI to learn from',
    color: '#22c55e',
    icon: <ShieldCheck size={18} aria-hidden />,
  },
  {
    label: 'Suspicious',
    meaning: 'Some seeding signals, not conclusive',
    care: 'Watch for a pattern across posts or accounts',
    color: '#eab308',
    icon: <AlertTriangle size={18} aria-hidden />,
  },
  {
    label: 'Likely seeded',
    meaning: 'Strong signals of planted or AI-generated promotion',
    care: 'A competitor attack, or buzz that will not hold up',
    color: '#F90B8A',
    icon: <Ban size={18} aria-hidden />,
  },
]

export function RedditSeedingTiersDiagram() {
  return (
    <div
      data-blog-layout='narrow'
      className='my-10 rounded-2xl border border-border/60 bg-muted/50 p-6 md:p-8'
    >
      <div className='mb-6 flex items-start gap-3'>
        <div
          className='flex h-10 w-10 shrink-0 items-center justify-center rounded-lg'
          style={{ backgroundColor: '#008dcc15', color: '#008dcc' }}
        >
          <Bot size={18} aria-hidden />
        </div>
        <div>
          <p className='text-xs font-semibold uppercase tracking-wider text-muted-foreground'>
            Seeding detection: three tiers
          </p>
          <p className='mt-1 text-sm leading-relaxed text-foreground/80'>
            Every Reddit mention is scored for seeding risk using account age,
            karma, brand-name density, and writing patterns, then sorted into
            three tiers.
          </p>
        </div>
      </div>

      <div className='space-y-3'>
        {TIERS.map(t => (
          <div
            key={t.label}
            className='rounded-xl border border-border/60 bg-background p-5'
            style={{ borderLeftWidth: '4px', borderLeftColor: t.color }}
          >
            <div className='flex items-center gap-2 text-foreground'>
              <span style={{ color: t.color }}>{t.icon}</span>
              <span className='text-base font-semibold leading-none'>
                {t.label}
              </span>
            </div>
            <p className='mt-2 text-sm font-medium text-foreground/80'>
              {t.meaning}
            </p>
            <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
              {t.care}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}
